import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Slider,
  CircularProgress,
} from "@mui/material";

const fetchBlob = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Fetch failed");
  return await res.blob();
};

const formatTime = (sec) => {
  if (!isFinite(sec)) return "00:00";
  const s = Math.max(0, sec);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 1000);
  return (
    String(mm).padStart(2, "0") +
    ":" +
    String(ss).padStart(2, "0") +
    (ms ? "." + String(ms).padStart(3, "0") : "")
  );
};

const recordTrimmedWebM = async (srcBlob, startSec, endSec) => {
  const url = URL.createObjectURL(srcBlob);
  const video = document.createElement("video");
  video.src = url;
  video.crossOrigin = "anonymous";
  video.playsInline = true;

  await new Promise((res, rej) => {
    const onError = () => rej(new Error("metadata error"));
    video.addEventListener("loadedmetadata", res, { once: true });
    video.addEventListener("error", onError, { once: true });
  });

  const duration = video.duration || (endSec > startSec ? endSec : 0);
  const start = Math.max(0, Math.min(startSec, Math.max(0, duration - 0.01)));
  const end = Math.max(start + 0.05, Math.min(endSec, duration));
  video.currentTime = start;
  await new Promise((res) =>
    video.addEventListener("seeked", res, { once: true })
  );

  let stream;
  if (video.captureStream) {
    stream = video.captureStream();
  } else if (video.mozCaptureStream) {
    stream = video.mozCaptureStream();
  } else {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    const draw = () => {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      if (!video.paused && !video.ended) requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);
    stream = canvas.captureStream(30);
  }

  const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
    ? "video/webm;codecs=vp9,opus"
    : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
    ? "video/webm;codecs=vp8,opus"
    : "video/webm";

  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: 3_000_000,
  });
  const chunks = [];
  recorder.ondataavailable = (e) =>
    e.data && e.data.size && chunks.push(e.data);

  const stopRecording = () =>
    new Promise((resolve) => {
      recorder.onstop = resolve;
      recorder.state !== "inactive" && recorder.stop();
    });

  try {
    await video.play();
  } catch {
    video.muted = true;
    await video.play();
  }
  recorder.start();

  const done = new Promise((resolve) => {
    const onTime = () => {
      if (video.currentTime >= end || video.ended) {
        video.pause();
        video.removeEventListener("timeupdate", onTime);
        resolve();
      }
    };
    video.addEventListener("timeupdate", onTime);
  });

  await done;
  await stopRecording();

  const out = new Blob(chunks, { type: mime });
  URL.revokeObjectURL(url);
  return out;
};

const HandleTrimPromoVideo = ({ open, src, onCancel, onSave }) => {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [err, setErr] = useState("");
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState([0, 0]);

  useEffect(() => {
    let ignore = false;
    const boot = async () => {
      setErr("");
      setLoading(true);
      try {
        if (!src) throw new Error("No video source");
        const blob = await fetchBlob(src);
        if (!ignore)
          setFile(
            new File([blob], "input", { type: blob.type || "video/mp4" })
          );
      } catch {
        if (!ignore)
          setErr(
            "Can't read video bytes (CORS). Pick a local file, then trim."
          );
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    if (open) boot();
    return () => {
      ignore = true;
      setFile(null);
      setDuration(0);
      setRange([0, 0]);
      setErr("");
      setTrimming(false);
    };
  }, [open, src]);

  const onLoadedMeta = () => {
    const d = videoRef.current?.duration || 0;
    setDuration(d);
    setRange([0, d]);
  };

  const canTrim = useMemo(() => {
    const [s, e] = range;
    return (
      !!file && duration > 0 && e - s > 0.2 && !loading && !err && !trimming
    );
  }, [file, duration, range, loading, err, trimming]);

  const handleTrim = async () => {
    setErr("");
    try {
      const [start, end] = range;
      if (!file || end - start <= 0.2) return;
      setTrimming(true);
      const out = await recordTrimmedWebM(
        file,
        Math.max(0, start),
        Math.min(end, duration)
      );
      onSave?.(out);
    } catch {
      setErr("Trimming failed. Try again or select a shorter segment.");
    } finally {
      setTrimming(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={trimming ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Trim Video</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {loading && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Loading video…
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {!!err && <Alert severity="error">{err}</Alert>}

          <Box
            sx={{
              width: "100%",
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#000",
            }}
          >
            <video
              ref={videoRef}
              src={src}
              controls
              style={{ width: "100%", height: 320, objectFit: "cover" }}
              onLoadedMetadata={onLoadedMeta}
            />
          </Box>

          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Select Range ({formatTime(range[0])} – {formatTime(range[1])} of{" "}
              {formatTime(duration)})
            </Typography>
            <Slider
              min={0}
              max={Math.max(0, duration)}
              step={0.01}
              value={range}
              onChange={(_, v) => setRange(v)}
              disableSwap
              disabled={trimming}
            />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">
                Start: {formatTime(range[0])}
              </Typography>
              <Typography variant="caption">
                End: {formatTime(range[1])}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={trimming}>
          Cancel
        </Button>
        <Button onClick={handleTrim} variant="contained" disabled={!canTrim}>
          {trimming ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={18} />
              <span>Trimming…</span>
            </Stack>
          ) : (
            "Trim & Use"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HandleTrimPromoVideo;
